import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  ApiError,
  composeAddress,
  listPublicProjectAddresses,
  makeApi,
  resolveProject,
  type Me,
  type PublicAddress,
  type PublicProject,
} from '../api/client';
import { config } from '../config';

type AddStep = 'idle' | 'credentials' | 'address';

export function ProfilePage(): JSX.Element {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Two-step add-address flow (advanced tier).
  const [addStep, setAddStep] = useState<AddStep>('idle');
  const [projectReferenceNumber, setProjectReferenceNumber] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [project, setProject] = useState<PublicProject | null>(null);
  const [addresses, setAddresses] = useState<PublicAddress[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [addressFilter, setAddressFilter] = useState('');
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [adding, setAdding] = useState(false);

  const reload = async (): Promise<void> => {
    try {
      const m = await api.me();
      setMe(m);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    }
  };

  useEffect(() => {
    reload();
  }, []);

  useEffect(() => {
    if (addStep !== 'address' || !project) return;
    setLoadingAddresses(true);
    listPublicProjectAddresses(project.pid)
      .then((r) => setAddresses(r.addresses))
      .catch(() => setAddresses([]))
      .finally(() => setLoadingAddresses(false));
  }, [addStep, project]);

  const filteredAddresses = useMemo(() => {
    const q = addressFilter.trim().toLowerCase();
    if (!q) return addresses;
    return addresses.filter((a) => composeAddress(a).toLowerCase().includes(q));
  }, [addresses, addressFilter]);

  function resetAddFlow(): void {
    setAddStep('idle');
    setProjectReferenceNumber('');
    setProject(null);
    setAddresses([]);
    setAddressFilter('');
    setSelectedAddressId('');
    setResolveError(null);
  }

  async function onResolve(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setResolveError(null);
    if (!projectReferenceNumber.trim()) return;
    setResolving(true);
    try {
      const p = await resolveProject(projectReferenceNumber.trim());
      setProject(p);
      setSelectedAddressId('');
      setAddressFilter('');
      setAddStep('address');
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setResolveError(
          `We couldn't find a project with that reference number. Contact support at ${config.supportEmail} if you need help.`,
        );
      } else {
        setResolveError(err instanceof Error ? err.message : 'Could not verify the project.');
      }
    } finally {
      setResolving(false);
    }
  }

  async function onAdd(): Promise<void> {
    if (!selectedAddressId) return;
    setAdding(true);
    setError(null);
    try {
      await api.addMyAddress(selectedAddressId);
      resetAddFlow();
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add');
    } finally {
      setAdding(false);
    }
  }

  // Defaults to on — matches the server-side default applied to new
  // signups and to legacy rows that pre-date the field.
  const savedPrefs = me?.notificationPreferences ?? { emailOnStaffComment: true };
  const [draftEmailOnStaffComment, setDraftEmailOnStaffComment] = useState<boolean | null>(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [prefsError, setPrefsError] = useState<string | null>(null);
  const [prefsSavedAt, setPrefsSavedAt] = useState<number | null>(null);

  // Reset the draft whenever the server state changes (e.g. after a reload).
  useEffect(() => {
    setDraftEmailOnStaffComment(null);
  }, [savedPrefs.emailOnStaffComment]);

  const effectiveEmailOnStaffComment =
    draftEmailOnStaffComment ?? savedPrefs.emailOnStaffComment;
  const prefsDirty = draftEmailOnStaffComment !== null
    && draftEmailOnStaffComment !== savedPrefs.emailOnStaffComment;

  async function savePrefs(): Promise<void> {
    if (!me || draftEmailOnStaffComment === null) return;
    setPrefsError(null);
    setSavingPrefs(true);
    try {
      const r = await api.updateMyNotificationPreferences({
        emailOnStaffComment: draftEmailOnStaffComment,
      });
      setMe((curr) =>
        curr ? { ...curr, notificationPreferences: r.notificationPreferences } : curr,
      );
      setDraftEmailOnStaffComment(null);
      setPrefsSavedAt(Date.now());
    } catch (e) {
      setPrefsError(e instanceof Error ? e.message : 'Failed to update preferences');
    } finally {
      setSavingPrefs(false);
    }
  }

  function discardPrefChanges(): void {
    setDraftEmailOnStaffComment(null);
    setPrefsError(null);
  }


  if (!me) return <div className="text-gray-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">My profile</h1>
      <div className="card space-y-2">
        <div>
          <span className="text-gray-400 text-sm">Email: </span>
          <span className="font-medium text-white">{me.email}</span>
        </div>
        <div>
          <span className="badge bg-gray-800 text-gray-300 border border-gray-700">{me.accountStatus}</span>{' '}
          <span className="badge bg-gray-800 text-gray-300 border border-gray-700">tier: {me.accountTier}</span>
          {me.flagged && (
            <span className="badge bg-red-900/60 text-red-300 border border-red-700/50 ml-2">flagged</span>
          )}
        </div>
        {me.flagReasons.length > 0 && (
          <div className="text-xs text-gray-500">
            Reasons: {me.flagReasons.join(', ')}
          </div>
        )}
      </div>

      <div className="card space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-white">My addresses</h2>
        </div>
        <div className="space-y-2">
          {me.addresses.length === 0 && (
            <div className="text-gray-500 text-sm">No addresses linked yet.</div>
          )}
          {me.addresses.map((a) => (
            <div
              key={a.addressId}
              className="border border-gray-700 rounded-md p-3 flex justify-between items-start gap-3 bg-gray-800/40"
            >
              <div>
                <div className="font-medium text-white">{composeAddress(a)}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {a.claimStatus === 'primary' && (
                    <span className="badge bg-emerald-900/60 text-emerald-300 border border-emerald-700/50">primary</span>
                  )}
                  {a.claimStatus === 'contested' && (
                    <span className="badge bg-red-900/60 text-red-300 border border-red-700/50">awaiting review</span>
                  )}
                  {a.claimStatus === 'invalid' && (
                    <span className="badge bg-red-900/60 text-red-300 border border-red-700/50">invalid</span>
                  )}
                  {a.projects.map((p) => (
                    <span
                      key={p.projectId}
                      className="badge bg-gray-800 text-gray-300 border border-gray-700"
                    >
                      {p.displayName} ({p.projectReferenceNumber})
                    </span>
                  ))}
                </div>
                {a.claimStatus !== 'primary' && (
                  <div className="text-xs text-gray-500 mt-1">
                    Contact{' '}
                    <a className="underline text-red-400 hover:text-red-300" href={`mailto:${config.supportEmail}`}>
                      {config.supportEmail}
                    </a>{' '}
                    if this looks wrong.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {me.accountTier === 'advanced' && (
          <div className="pt-3 border-t border-gray-800 space-y-2">
            {addStep === 'idle' && (
              <button className="btn-secondary" onClick={() => setAddStep('credentials')}>
                Add another address
              </button>
            )}

            {addStep === 'credentials' && (
              <form onSubmit={onResolve} className="space-y-2">
                <h3 className="font-medium text-white">Add another address</h3>
                <label className="label" htmlFor="profile-project-ref">Project reference number</label>
                <input
                  id="profile-project-ref"
                  className="input"
                  type="text"
                  value={projectReferenceNumber}
                  onChange={(e) => setProjectReferenceNumber(e.target.value.toUpperCase())}
                  placeholder="e.g. TARANTO-1"
                  autoComplete="off"
                  required
                />
                <div className="text-xs text-gray-500">
                  Enter the project reference number from the email for the additional property.
                </div>
                {resolveError && <div className="text-sm text-red-400">{resolveError}</div>}
                <div className="flex gap-2">
                  <button type="button" className="btn-secondary" onClick={resetAddFlow}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={resolving}>
                    {resolving ? 'Verifying…' : 'Continue'}
                  </button>
                </div>
              </form>
            )}

            {addStep === 'address' && project && (
              <div className="space-y-2">
                <h3 className="font-medium text-white">Add another address</h3>
                <div className="bg-gray-800 border border-gray-700 rounded-md p-3 text-sm">
                  <div className="font-medium text-white">{project.displayName}</div>
                  <div className="text-gray-300">{project.mainAddress}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Project reference number: {project.projectReferenceNumber}
                  </div>
                </div>
                <label className="label" htmlFor="profile-address-filter">Filter</label>
                <input
                  id="profile-address-filter"
                  className="input"
                  type="text"
                  value={addressFilter}
                  onChange={(e) => setAddressFilter(e.target.value)}
                  placeholder="Type to filter"
                  autoComplete="off"
                />
                <label className="label" htmlFor="profile-address-select">Address</label>
                {loadingAddresses ? (
                  <div className="text-sm text-gray-500">Loading…</div>
                ) : (
                  <select
                    id="profile-address-select"
                    className="input"
                    value={selectedAddressId}
                    onChange={(e) => setSelectedAddressId(e.target.value)}
                    size={Math.min(8, Math.max(4, filteredAddresses.length))}
                  >
                    {filteredAddresses.map((a) => (
                      <option key={a.addressId} value={a.addressId}>
                        {composeAddress(a)}
                      </option>
                    ))}
                  </select>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setAddStep('credentials')}
                    disabled={adding}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={onAdd}
                    disabled={!selectedAddressId || adding}
                  >
                    {adding ? 'Adding…' : 'Add address'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {me.accountTier === 'standard' && (
          <div className="text-xs text-gray-500 pt-3 border-t border-gray-800">
            Contact support to upgrade to an advanced account if you need to register multiple
            properties (e.g. for landlords).
          </div>
        )}
      </div>

      <div className="card space-y-3">
        <h2 className="font-semibold text-white">Email notifications</h2>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={effectiveEmailOnStaffComment}
            onChange={(e) => setDraftEmailOnStaffComment(e.target.checked)}
            disabled={savingPrefs}
            aria-label="Email me when staff or admin comment on my issues"
          />
          <span className="text-sm text-gray-300">
            Email me when staff or admin comment on my issues.
            <span className="block text-xs text-gray-500 mt-1">
              Emails are sent from a do-not-reply address — sign in to the portal to respond.
            </span>
          </span>
        </label>
        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            className="btn-primary"
            onClick={savePrefs}
            disabled={!prefsDirty || savingPrefs}
          >
            {savingPrefs ? 'Saving…' : 'Save changes'}
          </button>
          {prefsDirty && !savingPrefs && (
            <button
              type="button"
              className="btn-secondary"
              onClick={discardPrefChanges}
            >
              Discard
            </button>
          )}
          {!prefsDirty && !savingPrefs && prefsSavedAt && (
            <span className="text-xs text-gray-500">Saved.</span>
          )}
        </div>
        {prefsError && <div className="text-sm text-red-400">{prefsError}</div>}
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}
    </div>
  );
}
